<template>
  <TimePickerRange
    v-if="type === 'time'"
    ref="timeSelect"
    :key="dateKey"
    v-model="selfValue"
    range-separator="至"
    :format="format"
    :start-placeholder="startPlaceholder"
    :end-placeholder="endPlaceholder"
    :disabled="Disabled"
    :size="Size"
    :pickerOptions="PickerOptions"
    :clearable="clearable"
  />

  <TimeSelectRange
    v-else-if="type === 'time-select'"
    ref="timeSelect"
    :key="dateKey"
    v-model="selfValue"
    range-separator="至"
    :start-placeholder="startPlaceholder"
    :end-placeholder="endPlaceholder"
    :disabled="Disabled"
    :size="Size"
    :pickerOptions="PickerOptions"
    :clearable="clearable"
  />

  <MonthRange
    v-else-if="type === 'month'"
    ref="monthRange"
    :key="dateKey"
    v-model="selfValue"
    range-separator="至"
    :start-placeholder="startPlaceholder"
    :end-placeholder="endPlaceholder"
    :disabled="Disabled"
    :size="Size"
    :clearable="clearable"
  />

  <DateRange
    v-else
    v-model="selfValue"
    :dateType="dateType"
    :value-format="format"
    :key="dateKey"
    ref="yearRange"
    range-separator="至"
    :start-placeholder="startPlaceholder"
    :end-placeholder="endPlaceholder"
    :disabled="Disabled"
    :size="Size"
    :pickerOptions="PickerOptions"
    :clearable="clearable"
  />
</template>

<script setup lang="ts">
import { useFormItem, useFormDisabled } from 'element-plus'
import MonthRange from './components/MonthRange.vue'
import DateRange from './components/DateRange.vue'
import TimeSelectRange from './components/TimeSelectRange.vue'
import TimePickerRange from './components/TimePickerRange.vue'

const { formItem, form } = useFormItem()

const placeholderMap = {
  time: '起始时间/结束时间', // 时间组件，精确到分钟
  'time-select': '起始时间/结束时间', // 时间组件，精确到分钟
  'date-time': '起始时间/结束时间', // 日期时间组件，精确到年月日时分秒
  date: '起始日期/结束日期', // 起始日期，结束日期，精确到秒
  'year-month': '起始月份/结束月份', // 年月，精确到月
  month: '起始月份/结束月份', // 月份范围，精确到月
  year: '起始年份/结束年份' //年份选择，精确到年
}

//各种类型的 format, type
const valueFormat = {
  time: {
    type: '',
    defaultFormat: 'HH:mm',
    allFormat: ['HH', 'HH:mm', 'HH:mm:ss']
  },
  'date-time': {
    type: 'datetime',
    defaultFormat: 'YYYY-MM-DD HH:mm:ss',
    allFormat: ['YYYY-MM-DD HH:mm:ss']
  },
  'time-select': {
    type: 'time-select',
    defaultFormat: 'HH:mm',
    allFormat: ['HH:mm']
  },
  date: {
    type: 'date',
    defaultFormat: 'YYYY-MM-DD HH:mm:ss',
    allFormat: ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD']
  },
  'year-month': {
    type: 'month',
    defaultFormat: 'YYYY-MM',
    allFormat: ['YYYY-MM']
  },
  month: {
    type: 'month',
    defaultFormat: 'MM',
    allFormat: ['MM']
  },
  year: {
    type: 'year',
    defaultFormat: 'YYYY',
    allFormat: ['YYYY']
  }
}

const props = defineProps({
  start: {
    type: [String, Number]
  },
  end: {
    type: [String, Number]
  },
  placeholder: {
    type: String
  },
  valueFormat: {
    type: String
  },
  type: {
    type: String,
    default: 'date'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  // ["", "default", "small", "large"]
  size: {
    type: String,
    default: ''
  },
  defaultDate: {
    type: [String, Array],
    default: ''
  },
  pickerOptions: {
    type: Object
  },
  clearable: {
    type: Boolean,
    default: true
  }
})

const PickerOptions = computed(() => ({
  start: '00:00',
  step: '00:60',
  end: '23:00',
  ...props.pickerOptions
}))

// 处理传入的placeholder
const startPlaceholder = ref('请选择')
const endPlaceholder = ref('请选择')
watch(() => props.placeholder, (n) => {
  let pl = n || placeholderMap[props.type]
  if(pl?.includes('/')) {
    startPlaceholder.value = pl.split('/')[0]
    endPlaceholder.value = pl.split('/')[1]
  }
}, {
  immediate: true
})

// 是否需要在输入的时候改变key，解决v-model无法双向绑定问题
let dateKeyChange = ref(true)
let selfValue = ref(['', ''])
let format = ref('')
let dateType = ref('')
let dateKey = ref(Math.random())

const emits = defineEmits(['update:start', 'update:end', 'change'])

handleValueFormat()

// 同步父组件的disabled
const formDisabled = useFormDisabled()
const Disabled = computed(() => {
  return props.disabled || formDisabled.value
})

// 组件大小，可以单独控制，或者拉取form组件大小
const Size = computed(() => {
  return props.size || form?.size
})

// 监听值改变
watch(
  () => selfValue.value,
  (newVal, oldVal) => {
    dateKeyChange.value && (dateKey.value = Math.random())
    let time = ['', '']
    if (Array.isArray(newVal) && newVal.length == 2) {
      newVal.forEach((item, index) => {
        if (typeof item === 'string' && item !== '') {
          time[index] = item
        }
      })
    }

    if (props.type === 'date' && time[0] !== '' && format.value === 'YYYY-MM-DD HH:mm:ss') {
      emits('update:start', time[0])
      emits('update:end', time[1].replace('00:00:00', '23:59:59'))
      emits('change', [time[0], time[1].replace('00:00:00', '23:59:59')])
    } else {
      emits('update:start', time[0])
      emits('update:end', time[1])
      emits('change', time)
    }

    if (newVal === null || newVal === undefined) {
      selfValue.value = ['', '']
    }

    formItem?.validate('change') // 触发父组件表单校验
  },
  {
    deep: true
  }
)

watch(
  () => props.start,
  (n, o) => {
    dateKeyChange.value && (dateKey.value = Math.random())
    if ((typeof n === 'string' || typeof n === 'number') && props.end !== '' && n !== '') {
      selfValue.value[0] = n
    } else {
      selfValue.value[0] = ''
      selfValue.value[1] = ''
    }
  },
  {
    immediate: true
  }
)

watch(
  () => props.end,
  (n, o) => {
    dateKeyChange.value && (dateKey.value = Math.random())
    if ((typeof n === 'string' || typeof n === 'number') && props.start !== '' && n !== '') {
      selfValue.value[1] = n
    } else {
      selfValue.value[0] = ''
      selfValue.value[1] = ''
    }
  },
  {
    immediate: true
  }
)

// 处理format
function handleValueFormat() {
  let t = valueFormat[props.type]
  dateType.value = t.type
  if (props.valueFormat !== '' && t.allFormat.includes(props.valueFormat)) {
    format.value = props.valueFormat
  } else {
    format.value = t.defaultFormat
  }
}
</script>

<style lang="scss">
.time-range-picker {
  display: inline-flex;
  padding: 0 10px;
  overflow: hidden;
  width: var(--el-date-editor-width);
  &.is-disabled {
    cursor: not-allowed !important;
    background-color: var(--el-disabled-bg-color);
  }
  .el-input__wrapper {
    padding: 0;
    border: none;
    border-radius: 0;
    box-shadow: none !important;
    .el-input__prefix,
    .el-input__suffix {
      display: none;
    }
    .el-input__inner {
      text-align: center;
      border: none;
    }
  }
}
</style>
